import React, { useContext, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { AuthContext } from '../../context/auth.context'
import { formatCount } from '../sso-workspace/format'
import { ErrorReference } from '../error/error-reference'
import { getSession } from './audit-api'
import { auditErrorText } from './entry-format'
import { filtersToSearch, EMPTY_FILTERS } from './filters-model'
import { dateText, timeText, wallClock } from '../date/local-time'

// A sign-in session in words, never its UUID (owner 2026-10-07: "ugly"; he
// wants to see whether something happened in *this* session).
//
//   - "This session": the reader's own, the access token's sessionID claim;
//   - "Another session · Mac app · started Oct 7, 2:35 PM": what content's
//     GET /audit/session knows about it (the app it signed in with, and its
//     sign-in -- or, when the sign-in is not the reader's to see, the first
//     time it shows up there: "first seen ...");
//   - "Another session" when nothing is known.
//
// The full id stays in an entry's Details, for staff only (SessionDetail).
// Filtering by a session still works by its id, in the URL.

// The reader's own session id (the access token's sessionID claim), or ''.
export const currentSessionOf = state => String((state && state.jwtClaims && state.jwtClaims.sessionID) || '')

// Two ids name one session (a UUID, any case).
export const isSameSession = (a, b) => Boolean(a && b && String(a).trim().toLowerCase() === String(b).trim().toLowerCase())

// The reader's own session id, from the auth context ('' with none).
export const useCurrentSession = () => {
  const auth = useContext(AuthContext)
  return currentSessionOf(auth && auth.state)
}

// "Oct 7, 2:35 PM MDT" in the reader's time zone (shared/date); the year
// too when it isn't this year's.
export const sessionTime = (value, now = new Date()) => {
  const c = wallClock(value)
  if (!c) return ''
  return `${dateText(value, { year: c.year !== wallClock(now).year })}, ${timeText(value)}`
}

export const THIS_SESSION = 'This session'
export const ANOTHER_SESSION = 'Another session'

// The label: current (the reader's own session) wins; otherwise what the
// summary (GET /audit/session) knows.
export const sessionLabel = (current, summary, now = new Date()) => {
  if (current) return THIS_SESSION
  const parts = [ANOTHER_SESSION]
  if (summary && summary.app) parts.push(summary.app)
  if (summary && summary.startedAt) parts.push(`started ${sessionTime(summary.startedAt, now)}`)
  else if (summary && summary.firstSeenAt) parts.push(`first seen ${sessionTime(summary.firstSeenAt, now)}`)
  return parts.join(' · ')
}

// GET /audit/session, one read per question: the same session, scope and
// window asked again (a re-render, a second entry of the session) shares
// the first answer. A failed read is forgotten, so the next ask tries again.
const cache = new Map()
const MAX_CACHED = 50

export const resetSessionSummaries = () => cache.clear()

const readSummary = params => {
  const key = JSON.stringify(params)
  if (!cache.has(key)) {
    if (cache.size >= MAX_CACHED) cache.delete(cache.keys().next().value)
    const promise = Promise.resolve().then(() => getSession(params))
    cache.set(key, promise)
    promise.catch(() => cache.delete(key))
  }
  return cache.get(key)
}

// What content knows about sessionID, read in the reader's scope:
// params are the page's own (organization, person or group, window);
// others asks for the count in other organizations as well. Nothing is read
// when disabled, without a session, or for the reader's own session unless
// the count is wanted.
// -> { summary, error } (both null while loading)
export const useSessionSummary = (sessionID, params, { others = false, enabled = true } = {}) => {
  const current = isSameSession(sessionID, useCurrentSession())
  const ask = enabled && Boolean(sessionID) && (others || !current)
  const request = ask ? sessionRequest(sessionID, params, others) : null
  const key = request ? JSON.stringify(request) : ''
  const [state, setState] = useState({ key: '', summary: null, error: null })

  useEffect(() => {
    if (!key) return undefined
    let live = true
    readSummary(request).then(
      summary => live && setState({ key, summary: summary || {}, error: null }),
      error => live && setState({ key, summary: null, error })
    )
    return () => {
      live = false
    }
  }, [key]) // eslint-disable-line react-hooks/exhaustive-deps

  const mine = state.key === key && key !== ''
  const summary = mine ? state.summary : null
  // the token's claim decides at once; the server's answer backs it up
  return { current: current || Boolean(summary && summary.current), summary, error: mine ? state.error : null }
}

// The read's parameters: the page's scope and window (no paging, no other
// filter: the summary is about the session), the session, and others=1.
const SCOPE_KEYS = ['scope', 'orgID', 'person', 'group', 'from', 'to', 'includeAutomatic']
export const sessionRequest = (sessionID, params, others) => {
  const out = {}
  SCOPE_KEYS.forEach(k => {
    if (params && params[k] !== undefined && params[k] !== null && params[k] !== '') out[k] = params[k]
  })
  out.sessionID = sessionID
  if (others) out.others = 1
  return out
}

// The session's label, as a hook: the reader's own is "This session"
// without a read.
export const useSessionLabel = (sessionID, params, opts) => {
  const s = useSessionSummary(sessionID, params, opts)
  return { ...s, label: sessionLabel(s.current, s.summary) }
}

// "3 more actions in other organizations" (owner wording); "999+" past the
// server's cap.
export const elsewhereText = summary => {
  const n = summary && summary.otherOrganizations
  if (!n) return null
  const count = `${formatCount(n)}${summary.otherOrganizationsMore ? '+' : ''}`
  return `${count} more ${n === 1 && !summary.otherOrganizationsMore ? 'action' : 'actions'} in other organizations`
}

// The Internal tools audit log for one session, every organization, over the
// same time range (and automatic changes as shown) as the page.
export const staffSessionHref = (sessionID, filters) => {
  const f = filters || EMPTY_FILTERS
  const search = filtersToSearch({ ...EMPTY_FILTERS, session: sessionID, range: f.range, from: f.from, to: f.to, includeAutomatic: f.includeAutomatic })
  return `/admin/audit-log${search}`
}

// Under "Showing only <session>": the session's entries in other
// organizations. Staff get the count and a button to the Internal tools
// audit log on that session (every organization); an organization's admins
// the count alone -- content answers them only the number. A failed read
// says so, with its reference.
export const SessionElsewhere = ({ sessionID, summary, error, staff, sysPage, filters }) => {
  if (error) {
    return (
      <div className="au-elsewhere">
        <p className="text-danger small m-0" role="alert">{auditErrorText(error, "This session's details couldn't be loaded.")}</p>
        <ErrorReference error={error} />
      </div>
    )
  }
  const text = elsewhereText(summary)
  if (!text) return null
  return (
    <div className="au-elsewhere" role="status">
      <span>{text}.</span>
      {staff && <Link className="btn sw-btn btn-sm" to={staffSessionHref(sessionID, filters)}>{sysPage ? 'Show every organization' : 'Open in Internal tools'}</Link>}
    </div>
  )
}

// An entry's session in its Details: the label, read when Details opens,
// and for staff the full id beneath it (never the visible label).
export const SessionDetail = ({ sessionID, params, staff }) => {
  const { label, error } = useSessionLabel(sessionID, params)
  return (
    <span className="au-session">
      <span>{label}</span>
      {staff && <code className="au-session-id">{sessionID}</code>}
      {error && <span className="sw-hint small">{"Its app and start couldn't be loaded."}</span>}
      {error && <ErrorReference error={error} />}
    </span>
  )
}
